import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useProfile } from "@/hooks/useProfile";
import {
  feedbackDismissKey,
  useFeedbackCandidates,
} from "@/hooks/useFeedbackCandidates";

const FeedbackReminderCard = () => {
  const { data: profile, isLoading: isProfileLoading } = useProfile();
  const { data: candidates, isLoading: areCandidatesLoading } = useFeedbackCandidates(profile?.id);
  const [dismissedTicketIds, setDismissedTicketIds] = useState<string[]>([]);

  const pendingEvent = useMemo(
    () =>
      candidates?.find(
        (candidate) =>
          !candidate.submitted_at &&
          !dismissedTicketIds.includes(candidate.ticket_id) &&
          !localStorage.getItem(feedbackDismissKey(candidate.ticket_id)),
      ),
    [candidates, dismissedTicketIds],
  );

  if (isProfileLoading || areCandidatesLoading || !profile || !pendingEvent) return null;

  const dismiss = () => {
    localStorage.setItem(feedbackDismissKey(pendingEvent.ticket_id), Date.now().toString());
    setDismissedTicketIds((current) => [...current, pendingEvent.ticket_id]);
  };

  return (
    <Card className="relative mt-6 p-4 shadow-md">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-8 w-8 text-muted-foreground"
        onClick={dismiss}
        aria-label="Dismiss feedback reminder"
      >
        <X className="h-4 w-4" />
      </Button>
      <div className="flex gap-3 pr-8">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/15">
          <MessageSquareText className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-card-foreground">How was your Nimi?</h2>
          <p className="mt-1 truncate text-sm text-muted-foreground">
            Share feedback on {pendingEvent.event_title}
          </p>
        </div>
      </div>
      <Button asChild variant="outline" className="mt-4 w-full">
        <Link to={`/feedback?ticket=${pendingEvent.ticket_id}`}>Leave feedback</Link>
      </Button>
    </Card>
  );
};

export default FeedbackReminderCard;